import React, { useCallback, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import styled from 'styled-components';
import {
  Breadcrumbs,
  BreadcrumbsItem,
  H1,
  PageActions,
  PageHeader,
  PageHeaderAction,
  PageHeaderAvatar,
  PageHeaderAvatarLayout,
  PageHeaderContent,
  PaymentOverviewStats,
  PaymentStatusPills,
  Section,
  SectionHeader,
  Select,
  Text,
} from 'components';
import { theme, toRem } from 'design';
import { URL } from 'routes/routeConstants';
import { BusinessDayLabel } from 'screens/Payments/styles';
import { UserNames } from 'screens/UserDetail/components';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { getSiteEmployeePayoutsRequest, Payouts, selectPayouts, selectTotalCountPayouts } from 'store/Collections/Payouts';
import { getPayPeriodsRequest, PayPeriods, selectPayPeriods } from 'store/Collections/PayPeriods';
import { selectEmployee } from 'store/Employee';
import { selectCurrentEmployeeOrg, selectCurrentEmployeeSite } from 'store/Employers';
import { getSitePayoutStatsRequest, selectPayoutStats, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { ApiQueryOperator } from 'types';
import { getFirstElement } from 'utils/arrays';
import { formatDateForApi, formatDateForUI } from 'utils/date';
import { PayrollPeriodWrapper } from './components';
import { PayrollTipsUserDetailPayouts } from './PayrollTipsUserDetailPayouts';

const SiteName = styled(Text)`
  margin-top: ${toRem(4)};
  color: ${theme.color.neutral[600]};
  font-size: ${toRem(16)};
`;

interface IParams {
  id: string;
  startDate: string;
  endDate: string;
}

const ReportsEmployeePayrollTips: React.FC = () => {
  const { id, startDate, endDate } = useParams<IParams>();
  const dispatch = useDispatch();
  const history = useHistory();

  const currentOrganizationId = useSelector(selectCurrentEmployeeOrg)?.id;
  const currentSite = useSelector(selectCurrentEmployeeSite);
  const currentSiteId = currentSite?.id;

  const employeeDetail = useSelector(selectEmployee());
  const fullName = employeeDetail?.name || '';

  const isPaymentPeriodsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, PayPeriods.GET_PAY_PERIODS_REQUEST));
  const paymentPeriods = useSelector(selectPayPeriods({ orgId: currentOrganizationId, siteId: currentSiteId }));
  const reverseChronologicalPaymentPeriods = paymentPeriods && reverse(sortBy(paymentPeriods, ({ start }) => dayjs(start, 'YYYY-MM-DD', true).valueOf()));
  const DEFAULT_END_DATE = endDate;
  const DEFAULT_START_DATE = startDate;

  const defaultRecordsPerPage = 10;
  const defaultOffset = 0;
  const payouts = useSelector(selectPayouts());
  const payoutsTotal = useSelector(selectTotalCountPayouts());
  const [paymentHistoryRecordsPerPage, setPaymentHistoryRecordsPerPage] = useState<number>(defaultRecordsPerPage);
  const [paymentHistoryOffset, setPaymentHistoryOffset] = useState<number>(defaultOffset);
  const [paymentHistorySort, setPaymentHistorySort] = useState<string>('-payment_date');
  const [paymentHistoryStartDate, setPaymentHistoryStartDate] = useState(DEFAULT_START_DATE);
  const [paymentHistoryEndDate, setPaymentHistoryEndDate] = useState(DEFAULT_END_DATE);

  const isPayoutsLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Payouts.GET_SITE_EMPLOYEE_PAYOUTS_REQUEST));

  const payoutStats = useSelector(selectPayoutStats());
  const isLoadingPayoutStats = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_SITE_PAYOUT_STATS_REQUEST));

  useEffect(() => {
    if (currentOrganizationId && currentSiteId && !paymentPeriods) dispatch(getPayPeriodsRequest({ orgId: currentOrganizationId, siteId: currentSiteId }));
  }, [currentOrganizationId, currentSiteId, paymentPeriods, dispatch]);

  const fetchEmployeePayments = useCallback(() => {
    if (id && paymentHistoryStartDate && paymentHistoryEndDate && currentSiteId) {
      dispatch(
        getSiteEmployeePayoutsRequest(currentSiteId, id, {
          sort: paymentHistorySort,
          limit: paymentHistoryRecordsPerPage,
          offset: paymentHistoryOffset,
          q: [
            { key: 'payment_date', operator: ApiQueryOperator.gte, value: formatDateForApi(paymentHistoryStartDate) },
            { key: 'payment_date', operator: ApiQueryOperator.lte, value: formatDateForApi(paymentHistoryEndDate) },
          ],
        })
      );
    }
  }, [id, currentSiteId, dispatch, paymentHistoryOffset, paymentHistoryRecordsPerPage, paymentHistorySort, paymentHistoryStartDate, paymentHistoryEndDate]);

  const getEmployeePayoutStats = useCallback(() => {
    dispatch(getSitePayoutStatsRequest(currentSiteId, paymentHistoryStartDate, paymentHistoryEndDate, { q: [{ key: 'employee_id', operator: ApiQueryOperator.eq, value: id }] }));
  }, [id, currentSiteId, paymentHistoryStartDate, paymentHistoryEndDate]);

  useEffect(() => {
    if (id && paymentHistoryStartDate && paymentHistoryEndDate && currentSiteId) {
      fetchEmployeePayments();
      getEmployeePayoutStats();
    }
  }, [id, currentSiteId, paymentHistoryOffset, paymentHistoryRecordsPerPage, paymentHistorySort, paymentHistoryStartDate, paymentHistoryEndDate, fetchEmployeePayments, getEmployeePayoutStats]);

  const paymentPeriodOptions = reverseChronologicalPaymentPeriods?.map((period) => ({
    label: `${formatDateForUI(period.start)} - ${formatDateForUI(period.end)}`,
    value: period,
  }));

  const initialSelectedPeriodOption = paymentPeriodOptions?.find((p) => p.value.start === startDate && p.value.end === endDate) || getFirstElement(paymentPeriodOptions);
  const employeeLoading = isPayoutsLoading || employeeDetail?.id !== id;

  return (
    <>
      <PageHeader>
        <PageHeaderContent stackable>
          <>
            <PageHeaderAvatarLayout>
              <PageHeaderAvatar
                username={fullName}
                size="large"
                id={employeeDetail?.id}
                image={employeeDetail?.user?.avatarUrl}
                hasPaymentMethod={!!employeeDetail?.user?.card}
                isLoading={employeeLoading}
                isDismissed={!!employeeDetail?.deletedAt}
              />
              <UserNames>
                <Breadcrumbs>
                  <BreadcrumbsItem data-cy="payments-breadcrumblink" label="Reports" to={URL.ReportsPayPeriod} />
                  <BreadcrumbsItem data-cy="make-payments-breadcrumblink" label="Employee Report" />
                </Breadcrumbs>
                {employeeLoading ? <Skeleton width={154} /> : <H1 marginBottom={0}>{fullName}</H1>}
                {employeeLoading ? <Skeleton width={100} /> : <SiteName>{currentSite?.name}</SiteName>}
              </UserNames>
            </PageHeaderAvatarLayout>

            <PageActions>
              {!employeeLoading && !employeeDetail?.deletedAt && employeeDetail?.sites && employeeDetail?.sites.find((s) => s.id === currentSiteId) && (
                <PageHeaderAction isLoading={employeeLoading} label="View User Details" appearance="tertiary" to={`/users/${employeeDetail?.id}`} />
              )}
            </PageActions>
          </>
        </PageHeaderContent>
      </PageHeader>

      <Section data-cy="table">
        <SectionHeader title="Payments" value={`(${payoutsTotal || 0})`} isLoading={isPaymentPeriodsLoading || isPayoutsLoading}>
          <BusinessDayLabel>{isPaymentPeriodsLoading || isPayoutsLoading ? <Skeleton width={90} /> : 'Payroll Period'}</BusinessDayLabel>
          <PayrollPeriodWrapper>
            <Select
              data-cy="periods-select"
              placeholder="Select payroll period"
              options={paymentPeriodOptions || []}
              values={[initialSelectedPeriodOption]}
              onChange={(selectedOptions) => {
                if (selectedOptions?.[0]) {
                  setPaymentHistoryStartDate(selectedOptions?.[0].value.start);
                  setPaymentHistoryEndDate(selectedOptions?.[0].value.end);
                }
                setPaymentHistoryOffset(defaultOffset);
                history.replace({
                  pathname: `${URL.ReportsEmployeePayPeriod}/${id}/${selectedOptions?.[0].value.start}/${selectedOptions?.[0].value.end}`,
                });
              }}
            />
          </PayrollPeriodWrapper>
        </SectionHeader>

        <PaymentStatusPills payoutStats={payoutStats} isLoading={isLoadingPayoutStats} />
        <PaymentOverviewStats payoutStats={payoutStats} isLoading={isLoadingPayoutStats} hideHouseAccount />

        <PayrollTipsUserDetailPayouts
          payouts={payouts}
          recordsPerPage={paymentHistoryRecordsPerPage}
          setRecordsPerPage={setPaymentHistoryRecordsPerPage}
          offset={paymentHistoryOffset}
          setOffset={setPaymentHistoryOffset}
          sort={paymentHistorySort}
          setSort={setPaymentHistorySort}
          totalRecords={payoutsTotal}
          isLoading={isPaymentPeriodsLoading || isPayoutsLoading}
          onUpdate={getEmployeePayoutStats}
          appearance="default"
        />
      </Section>
    </>
  );
};

export default ReportsEmployeePayrollTips;
